<script setup lang="ts">
import { parseISO } from 'date-fns';
import { isNil } from 'lodash-es';
import * as uuid from 'uuid';
import { computed, markRaw, onMounted, onUnmounted, ref } from 'vue';

import { isSeven } from '@/business_specification/20220824-seven';
import { customizeResponsiveBannerImageBySpecificTitle, isCosme } from '@/business_specification/20241122-cosme';
import {
  insertAppLinkToSpecificTextModule,
  isMorecon,
} from '@/business_specification/periodically-insert-app-link-for-morecon';
import {
  insertQuestionnaireToSpecificTextModule,
  isStarbucks,
} from '@/business_specification/periodically-insert-questionnaire-for-starbucks';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary.vue';
import { useAbly } from '@/composables/RealtimeService/useAbly.js';
import { usePubnub } from '@/composables/RealtimeService/usePubnub.js';
import { setRefreshTime } from '@/composables/useGlobalRefresh';
import { useTraceScheduledPostBug } from '@/composables/useTraceScheduledPostBug';
import { IS_GENERAL_CMS } from '@/js/constants';
import { tre } from '@/plugins/bugsnag';
import { DEVICE_BREAKPOINT, deviceBreakpoint, setContainerEl } from '@/store/breakpoint';
import { useTemplate } from '@/store/templateData';
import { findNextScheduledPost } from '@/utils/findNextScheduledPost';

// 動態載入所有模組組件
const modules = import.meta.glob('@/components/**/Module*Display.vue', { eager: true });

// 建立支援的模組組件映射，使用組件的 name 屬性
const moduleComponentsMap: Record<string, any> = {};
Object.entries(modules).forEach(([path, module]: [string, any]) => {
  if (module.default && module.default.__name) {
    moduleComponentsMap[module.default.__name] = markRaw(module.default);
  }
});

const getModuleComponent = (module: { name: string }) => {
  const componentName = `Module${module.name}Display`;
  return moduleComponentsMap[componentName] || null;
};

const BREAKPOINT_CLASS_NAME_MAPPING = {
  [DEVICE_BREAKPOINT.MOBILE]: 'mobile',
  [DEVICE_BREAKPOINT.TABLET]: 'tablet',
  [DEVICE_BREAKPOINT.LAPTOP]: 'laptop',
  [DEVICE_BREAKPOINT.DESKTOP]: 'desktop',
};

const mainEl = ref(null);

onMounted(() => {
  // TODO: 目前因影響範圍過大，所以先客製，但之後預期有地方讓使用者自己選擇參照瀏覽器寬度或是容器寬度
  if (isSeven()) {
    setContainerEl(mainEl.value);
  } else {
    setContainerEl(document.documentElement);
  }
});

const calcBreakpoint = computed(() => {
  return BREAKPOINT_CLASS_NAME_MAPPING[deviceBreakpoint.value] ?? '';
});

const { visibleModuleList, handsupConfig, realtimeServiceProvider } = useTemplate();

const realtimeServiceType = computed(() => realtimeServiceProvider.value?.type ?? null);
const realtimeServiceSubscribeKey = computed(() => realtimeServiceProvider.value?.subscribe_key ?? null);
const realtimeServiceSubscribeChannels = computed(() => realtimeServiceProvider.value?.channel_list ?? []);

let realtimeService: ReturnType<typeof useAbly> | ReturnType<typeof usePubnub> | null = null;

// NOTE: 目前只有一個通知事件，主要是用來通知 General CMS 有排程貼文更新
const NOTIFICATION_GENERAL_CMS_SCHEDULED_POSTS_UPDATE = 'general-cms-scheduled-posts-update';
const { notify } = useTraceScheduledPostBug(`PubNub::${NOTIFICATION_GENERAL_CMS_SCHEDULED_POSTS_UPDATE}`);

onMounted(() => {
  // NOTE: 目前只有 General CMS 會使用到
  if (!IS_GENERAL_CMS) { return; }
  if (!realtimeServiceType.value || !realtimeServiceSubscribeKey.value) { return; }

  if (realtimeServiceType.value === 'ably') {
    realtimeService = useAbly(
      realtimeServiceSubscribeKey.value,
      `hu-cms-${uuid.v4()}`,
    );
  } else if (realtimeServiceType.value === 'pubnub') {
    realtimeService = usePubnub(
      realtimeServiceSubscribeKey.value,
      `hu-cms-${uuid.v4()}`,
    );
  } else {
    tre(new Error(`Unknown realtime service type: ${realtimeServiceType.value}`));
    return;
  }

  realtimeService.subscribePostChannels(realtimeServiceSubscribeChannels.value);
  realtimeService.onMessageEvent((msg: any) => {
    const messageType = msg.message.type;

    if (messageType !== NOTIFICATION_GENERAL_CMS_SCHEDULED_POSTS_UPDATE) { return; }

    const updatedScheduledPosts = msg.message.data;
    const postId = handsupConfig.value?.post?.id ?? null;

    const isCurrentPostInUpdatedScheduledPosts =
      Boolean(updatedScheduledPosts.find((post: any) => post.post_id === postId));

    notify({
      updatedScheduledPosts,
      postId,
      now: (new Date()).toISOString(),
    });

    if (!isNil(postId) && !isCurrentPostInUpdatedScheduledPosts) {
      // 如果正在播的貼文被刪掉了但下一場還沒開始，則強制重整
      setRefreshTime(Date.now());
      return;
    }

    const nextScheduledPost = findNextScheduledPost(postId, updatedScheduledPosts);
    const nextScheduledPostId = nextScheduledPost?.post_id ?? null;
    if (postId === nextScheduledPostId) { return; }

    if (isNil(nextScheduledPost)) {
      setRefreshTime(Date.now());
    } else {
      setRefreshTime(parseISO(nextScheduledPost.scheduled_started_at).getTime());
    }
  });
});
onUnmounted(() => realtimeService?.destroy());

onMounted(() => {
  const postId = handsupConfig.value?.post?.id ?? null;
  if (postId !== null && isStarbucks()) {
    insertQuestionnaireToSpecificTextModule(postId);
  }

  if (postId !== null && isMorecon()) {
    insertAppLinkToSpecificTextModule(postId);
  }

  if (isCosme()) {
    customizeResponsiveBannerImageBySpecificTitle();
  }
});
</script>

<template>
<!---
  原本 laptop 是放在 component is 裡面設定為 class，再交給子模組判斷，
  但經過討論後改為放在此層並用 theme 的方式處理
  討論串: https://17media.slack.com/archives/G01AB5XUVNW/p1637901291174900
-->
<main class="main-layout" ref="mainEl" :class="calcBreakpoint">
  <ErrorBoundary>
    <component
      class="module-container"
      v-for="module in visibleModuleList"
      :key="module.id"
      :is="getModuleComponent(module)"
      :module="module"
    />
  </ErrorBoundary>
</main>
</template>
<style lang="scss" scoped>
.main-layout {
  .module-container {
    @apply mx-auto;
  }
}
</style>
